"use client";

import { useState, useRef, useEffect, useTransition } from "react";
import { useAdmin } from "@/lib/admin/context";
import { Icon } from "@/lib/icons/Icon";
import { logoutImperialAction } from "@/lib/admin/session-actions";
import { toast } from "@/lib/notifications/toast";
import styles from "./UserMenuDropdown.module.css";

/**
 * 👤 User Menu — dropdown z opcjami konta admina.
 *
 *   - "Twoje konto" -> link do `IADMIN?admin_page=your_administration_account` (nowa karta)
 *   - "Wyloguj" -> Server Action `logoutImperialAction` (POST /logout + cookie cleanup
 *     + revalidatePath). Side effect SSO: użytkownik jest też wylogowany z Imperial
 *     admin (single sign-out — jedno cookie = jedno wylogowanie).
 *
 * Logout flow:
 *   1. POST /IRESTAPI/{version}/IIAPI_CONTROLLER/logout z Bearer (z SSO bridge)
 *   2. Delete cookie IMPERIAL lokalnie
 *   3. `revalidatePath` + `router.refresh` -> Server Components re-render z anonymous
 *   4. AdminTopBar znika (brak `cms:edit` scope) -> user wraca do public view
 */
export function UserMenuDropdown() {
  const { user, adminPanelUrl, t } = useAdmin();
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const wrapperRef = useRef<HTMLDivElement>(null);

  //Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const displayName = user.name || user.login;
  /* Imperial admin page symbol: `your_administration_account` (linia mapuje
     na `ADMIN/pages/your_administration_account/` w Imperial backend). */
  const myAccountUrl = `${adminPanelUrl}?admin_page=your_administration_account`;

  const handleLogout = () => {
    setOpen(false);
    startTransition(async () => {
      const result = await logoutImperialAction();
      if (result.ok) {
        toast.success(t.topBar.userMenu.toastLoggedOut);
        /* Pełen browser reload zamiast router.refresh — Next.js client cache +
           Bearer'y w pamięci serwer SSO bridge'a (no-store fetch) gwarantowanie
           wyczyszczone. Plus reload re-applikuje cookie state (gdy cookie zostało
           usunięte ale Server Component widzi stary). User i tak wraca na stronę
           public po wylogowaniu — UX nie ucierpi. */
        window.location.reload();
      } else {
        toast.error(t.topBar.userMenu.toastError(result.error ?? t.topBar.userMenu.toastErrorUnknown));
      }
    });
  };

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Icon name="user-circle" className={styles.avatar} />
        <span className={styles.name}>{displayName}</span>
        <Icon name="caret-down" className={styles.chevron} />
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.userInfo}>
            <strong>{displayName}</strong>
            {user.email && <span>{user.email}</span>}
          </div>
          <a
            href={myAccountUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.menuItem}
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            {t.topBar.userMenu.yourAccount}
          </a>
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={handleLogout}
            disabled={isPending}
          >
            {isPending ? t.topBar.userMenu.loggingOut : t.topBar.userMenu.logout}
          </button>
        </div>
      )}
    </div>
  );
}
